import {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useRef,
  useCallback,
  type ChangeEvent,
} from 'react'
import { Download, FileIcon, ImageIcon, Trash2, UploadIcon } from 'lucide-react'
import type { SupabaseClient } from '@supabase/supabase-js'
import { Column, Row } from 'src/shared/components/Semantic/all'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'src/shared/components/ui/card'
import { Button } from 'src/shared/components/ui/button'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useSupabaseClient } from 'src/api/supabase'
import { useGetOrgId } from 'src/api/helpers/getOrgId'
import { useToast } from 'src/shared/hooks/use-toast'
import type { Database } from 'supabase/database.types'
import type { LogoFile, LogoFileType } from './types'
import {
  extToLogoFileType,
  formatFileSize,
  isAllowedVectorLogoExtension,
} from './utils'

const LOGO_BUCKET = 'illustration-logos'

/** Matches `LogoFileType` — extensions + MIME help EPS/AI in the system file picker */
const VECTOR_LOGO_INPUT_ACCEPT =
  '.eps,.ai,.svg,.pdf,.png,.jpg,.jpeg,image/svg+xml,application/pdf,application/postscript,application/illustrator,image/png,image/jpeg'

const PREVIEW_IMAGE_TYPES = new Set<LogoFileType>(['svg', 'png', 'jpg', 'jpeg'])

export interface LogosCardRef {
  reload: () => Promise<void>
}

function extFromFilename(name: string): string {
  return name.split('.').pop()?.toLowerCase() ?? ''
}

function listedFileToLogo(
  supabase: SupabaseClient<Database>,
  orgId: string,
  file: { name: string; metadata?: { size?: number } }
): LogoFile {
  const filePath = `${orgId}/${file.name}`
  const {
    data: { publicUrl },
  } = supabase.storage.from(LOGO_BUCKET).getPublicUrl(filePath)
  return {
    url: publicUrl,
    filePath,
    name: file.name,
    size: file.metadata?.size ?? 0,
    type: extToLogoFileType(extFromFilename(file.name)),
  }
}

export const VectorizedLogos = forwardRef<LogosCardRef>((_, ref) => {
  const { t } = useLocalization()
  const { toast } = useToast()
  const org_id = useGetOrgId()
  const supabase = useSupabaseClient()
  const [logos, setLogos] = useState<LogoFile[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const loadLogos = useCallback(async () => {
    if (!org_id) return

    try {
      const { data, error } = await supabase.storage
        .from(LOGO_BUCKET)
        .list(`${org_id}/`, {
          limit: 100,
          sortBy: { column: 'created_at', order: 'desc' },
        })

      if (error) throw error

      setLogos(
        (data ?? []).map((file) => listedFileToLogo(supabase, org_id, file))
      )
    } catch (err) {
      console.error('Error loading logos:', err)
    }
  }, [org_id, supabase])

  useEffect(() => {
    void loadLogos()
  }, [loadLogos])

  useImperativeHandle(ref, () => ({
    reload: loadLogos,
  }))

  const handleLogoUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !org_id) return

    const ext = extFromFilename(file.name)
    if (!ext || !isAllowedVectorLogoExtension(ext)) {
      toast({
        variant: 'destructive',
        title: t(
          'Use EPS, AI, SVG, PDF, PNG, or JPEG.',
          'Använd EPS, AI, SVG, PDF, PNG eller JPEG.'
        ),
      })
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    try {
      setIsUploading(true)
      const fileName = `${crypto.randomUUID()}.${ext}`
      const filePath = `${org_id}/${fileName}`

      const { error: uploadError } = await supabase.storage
        .from(LOGO_BUCKET)
        .upload(filePath, file)

      if (uploadError) throw uploadError

      const {
        data: { publicUrl },
      } = supabase.storage.from(LOGO_BUCKET).getPublicUrl(filePath)

      const newLogo: LogoFile = {
        url: publicUrl,
        filePath,
        name: file.name,
        size: file.size,
        type: extToLogoFileType(ext),
      }

      setLogos((prev) => [newLogo, ...prev])
      if (fileInputRef.current) fileInputRef.current.value = ''

      toast({
        variant: 'success',
        title: t('Logo uploaded', 'Logotyp uppladdad'),
      })
    } catch (err) {
      console.error('Error uploading logo:', err)
      toast({
        variant: 'destructive',
        title: t('Upload failed', 'Uppladdning misslyckades'),
      })
    } finally {
      setIsUploading(false)
    }
  }

  const handleDownloadLogo = async (logo: LogoFile) => {
    try {
      const res = await fetch(logo.url)
      if (!res.ok) throw new Error('fetch failed')

      const blob = await res.blob()
      const blobUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = logo.name
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(blobUrl)

      toast({
        variant: 'success',
        title: t('Logo downloaded', 'Logotyp nedladdad'),
      })
    } catch (err) {
      console.error('Error downloading logo:', err)
      toast({
        variant: 'destructive',
        title: t('Download failed', 'Nedladdning misslyckades'),
      })
    }
  }

  const handleDeleteLogo = async (logo: LogoFile) => {
    if (
      !org_id ||
      !confirm(
        t(
          'Are you sure you want to delete this logo?',
          'Är du säker på att du vill ta bort denna logotyp?'
        )
      )
    )
      return

    try {
      const { error } = await supabase.storage
        .from(LOGO_BUCKET)
        .remove([logo.filePath])

      if (error) throw error

      setLogos((prev) => prev.filter((l) => l.filePath !== logo.filePath))
      toast({
        variant: 'success',
        title: t('Logo deleted', 'Logotyp borttagen'),
      })
    } catch (err) {
      console.error('Error deleting logo:', err)
      toast({
        variant: 'destructive',
        title: t('Delete failed', 'Radering misslyckades'),
      })
    }
  }

  const triggerUpload = () => fileInputRef.current?.click()

  return (
    <>
      <Card>
        <CardHeader className="pb-4">
          <Row className="items-start gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted">
              <ImageIcon className="size-5 text-muted-foreground" />
            </div>
            <Column className="min-w-0 flex-1 gap-1">
              <CardTitle>
                {t('Vectorized logos', 'Vektoriserade logotyper')}
              </CardTitle>
              <CardDescription>
                {t(
                  'Upload vectorized logos that will be used by the factory in production and in the design view.',
                  'Ladda upp vektoriserade logotyper som kommer att användas av fabriken i produktion och i designvyn.'
                )}
              </CardDescription>
            </Column>
          </Row>
        </CardHeader>

        <CardContent>
          <Column className="gap-2">
            <Row className="items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">
                {t('Your uploads', 'Dina uppladdningar')}
              </span>
              {logos.length > 0 && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="h-8 shrink-0 gap-1.5 text-xs"
                  onClick={triggerUpload}
                  disabled={isUploading}
                >
                  <UploadIcon className="size-3.5" />
                  {t('Add logo', 'Lägg till logotyp')}
                </Button>
              )}
            </Row>

            {logos.length === 0 ? (
              <Column className="items-center gap-3 rounded-lg border border-dashed border-border bg-muted/15 px-4 py-7">
                <div className="flex size-11 items-center justify-center rounded-full bg-muted/80">
                  <UploadIcon className="size-5 text-muted-foreground" />
                </div>
                <Column className="items-center gap-1">
                  <span className="text-center text-sm font-medium">
                    {t('No uploads yet', 'Inga uppladdningar ännu')}
                  </span>
                  <span className="max-w-[260px] text-center text-xs text-muted-foreground">
                    {t(
                      'EPS, AI, SVG, PDF, or PNG / JPEG',
                      'EPS, AI, SVG, PDF eller PNG / JPEG'
                    )}
                  </span>
                </Column>
                <Button
                  size="sm"
                  className="gap-1.5"
                  onClick={triggerUpload}
                  disabled={isUploading}
                >
                  <UploadIcon className="size-4" />
                  {t('Upload file', 'Ladda upp fil')}
                </Button>
              </Column>
            ) : (
              <Column className="gap-2">
                {logos.map((logo) => (
                  <Row
                    key={logo.filePath}
                    className="items-center gap-3 rounded-lg border border-border/80 bg-background p-2.5 transition-colors hover:bg-muted/25"
                  >
                    <div className="flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted/40">
                      {PREVIEW_IMAGE_TYPES.has(logo.type) ? (
                        <img
                          src={logo.url}
                          alt={logo.name}
                          className="size-full object-contain"
                        />
                      ) : (
                        <FileIcon
                          className="size-9 text-muted-foreground"
                          aria-hidden
                        />
                      )}
                    </div>
                    <Column className="min-w-0 flex-1 gap-0.5">
                      <span className="truncate text-sm font-medium">
                        {logo.name}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {logo.type.toUpperCase()} · {formatFileSize(logo.size)}
                      </span>
                    </Column>
                    <Row className="shrink-0 gap-0.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8 text-muted-foreground hover:text-foreground"
                        onClick={() => handleDownloadLogo(logo)}
                        aria-label={t('Download', 'Ladda ner')}
                      >
                        <Download className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => handleDeleteLogo(logo)}
                        aria-label={t('Delete', 'Ta bort')}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </Row>
                  </Row>
                ))}
              </Column>
            )}
          </Column>
        </CardContent>
      </Card>

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept={VECTOR_LOGO_INPUT_ACCEPT}
        onChange={handleLogoUpload}
      />
    </>
  )
})

VectorizedLogos.displayName = 'VectorizedLogos'
